<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--titlegroup">
        <span class="a-header--title slide-down-enter-active">Aggregated Alert Detail</span>
        <HelpButton :content="HELP_CONTENT.changeEventDetail" />
      </span>
      <a-button size="default" class="a-header--button" @click="goBack"> <a-icon type="left" /> Back to list </a-button>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <template v-if="!isLoading">
      <div class="a-body flex-grow-1 overflow-y-auto p-20-px">
        <div class="ce-detail-head">
          <SeverityMix :severity="event.severity" :mix="severityMixParts" />
          <span class="ce-detail-head__title">{{ event.summary || 'Change on ' + (event.site || 'a website') }}</span>
          <a-tag v-if="event.has_malware" color="red">Malware found</a-tag>
          <a-tag :color="event.status === 'open' ? 'blue' : ''">{{ statusLabel }}</a-tag>
        </div>
        <p class="ce-detail-lead">
          {{ formatNumber(event.alert_count) }} alerts on {{ formatNumber(event.page_count) }} pages came from this one change. Nothing was
          deleted — every alert is listed below.
        </p>

        <a-row :gutter="16">
          <a-col :span="16">
            <a-descriptions bordered :column="2" size="small">
              <a-descriptions-item label="Website">{{ event.site || '-' }}</a-descriptions-item>
              <a-descriptions-item label="Category">{{ categoryLabel }}</a-descriptions-item>
              <a-descriptions-item label="Change Type">{{ changeTypeLabel }}</a-descriptions-item>
              <a-descriptions-item label="Severity">
                <SeverityMix :severity="event.severity" :mix="severityMixParts" />
              </a-descriptions-item>
              <a-descriptions-item label="First Alert">
                <template v-if="event.first_seen">{{ event.first_seen | dateString }} {{ event.first_seen | timeString }}</template>
                <template v-else>-</template>
              </a-descriptions-item>
              <a-descriptions-item label="Last Alert">
                <template v-if="event.last_seen">{{ event.last_seen | dateString }} {{ event.last_seen | timeString }}</template>
                <template v-else>-</template>
              </a-descriptions-item>
              <a-descriptions-item label="Alerts">{{ formatNumber(event.alert_count) }}</a-descriptions-item>
              <a-descriptions-item label="Pages">{{ formatNumber(event.page_count) }}</a-descriptions-item>
              <a-descriptions-item v-if="event.site_incident_id" label="Site-Wide Incident" :span="2">
                <router-link :to="'/change-events/site-incidents/' + event.site_incident_id">
                  #{{ event.site_incident_id }}
                </router-link>
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
          <a-col :span="8">
            <div class="ce-sidenote">
              <p class="ce-cardtitle">Alert Coverage</p>
              <p class="mb-0">All alerts are listed below. The group carries the severity of the most serious alert.</p>
            </div>
          </a-col>
        </a-row>

        <!-- The alert that started this change, and one alert that shows what it
             looks like. Both are named by their page, never by an id. -->
        <div v-if="seedAlert || exampleAlert" class="ce-panel">
          <p class="ce-cardtitle">Origin</p>
          <a-row :gutter="16">
            <a-col :span="12">
              <div v-if="seedAlert" class="ce-alertcard ce-alertcard--seed">
                <p class="ce-alertcard__kind">The alert that seeded this group. This group is defined by the seed.</p>
                <div class="ce-alertcard__line">
                  <a-tag class="ce-severity" :style="{ backgroundColor: severityColor(seedAlert.severity), borderColor: severityColor(seedAlert.severity) }">
                    {{ seedAlert.severityLabel }}
                  </a-tag>
                  <router-link class="ce-alertcard__name" :to="'/incident/detail/' + seedAlert.alertId">{{ seedAlert.name }}</router-link>
                </div>
                <p v-if="seedAlert.url" class="ce-alertcard__url">{{ seedAlert.url }}</p>
                <p v-if="seedAlert.detectedAt" class="ce-alertcard__when">
                  Seen {{ seedAlert.detectedAt | dateString }} {{ seedAlert.detectedAt | timeString }}
                </p>
                <p class="ce-alertcard__note">
                  Everything else below was put with this alert because it came from the same change.
                </p>
              </div>
              <p v-else class="ce-empty">The first alert of this change is not recorded.</p>
            </a-col>
            <a-col :span="12">
              <div v-if="exampleAlert" class="ce-alertcard">
                <p class="ce-alertcard__kind">One page this change hit</p>
                <div class="ce-alertcard__line">
                  <a-tag class="ce-severity" :style="{ backgroundColor: severityColor(exampleAlert.severity), borderColor: severityColor(exampleAlert.severity) }">
                    {{ exampleAlert.severityLabel }}
                  </a-tag>
                  <router-link class="ce-alertcard__name" :to="'/incident/detail/' + exampleAlert.alertId">{{ exampleAlert.name }}</router-link>
                </div>
                <p v-if="exampleAlert.url" class="ce-alertcard__url">{{ exampleAlert.url }}</p>
                <p v-if="exampleAlert.detectedAt" class="ce-alertcard__when">
                  Seen {{ exampleAlert.detectedAt | dateString }} {{ exampleAlert.detectedAt | timeString }}
                </p>
                <p class="ce-alertcard__note">Open it to see the change on the page itself.</p>
              </div>
            </a-col>
          </a-row>
        </div>

        <!-- What changed. The trigger files come first and are named as the
             cause, because that is the question a person asks here. -->
        <div v-if="triggerFiles.files.length || allChangedFiles.files.length || topFolders.length" class="ce-panel">
          <p class="ce-cardtitle">Files That Changed</p>
          <template v-if="triggerFiles.files.length">
            <p class="ce-fileshead">Trigger Files</p>
            <div class="ce-files">
              <a-tag v-for="file in triggerFiles.files" :key="'t-' + file" class="ce-file ce-file--trigger">{{ file }}</a-tag>
              <span v-if="triggerFiles.more" class="ce-files__more">and {{ formatNumber(triggerFiles.more) }} more</span>
            </div>
            <p class="ce-caption">
              The files that appeared or changed together on the pages below. They are the reason these alerts are one group.
            </p>
          </template>
          <template v-if="allChangedFiles.files.length">
            <p class="ce-fileshead">All Changed Files</p>
            <div class="ce-files">
              <a-tag v-for="file in allChangedFiles.files" :key="'a-' + file" class="ce-file">{{ file }}</a-tag>
              <span v-if="allChangedFiles.more" class="ce-files__more">and {{ formatNumber(allChangedFiles.more) }} more</span>
            </div>
          </template>
          <template v-if="topFolders.length">
            <p class="ce-fileshead">Folders</p>
            <div class="ce-files">
              <a-tag v-for="folder in topFolders" :key="'f-' + folder" class="ce-file">{{ folder }}</a-tag>
            </div>
          </template>
        </div>

        <p class="ce-cardtitle mt-4">Alerts in This Group</p>
        <div v-resize="elSizeChange" class="ce-alerts-table">
          <PaginationTable :clientPaging="false" :pagination="pagin" :columns="EventAlertsColumn" :dataSource="alerts" :tableSize="tableSize">
            <template slot="joinRuleTitle">
              <HeaderHelp
                label="Why Grouped"
                lead="Why this alert is in this group. Every alert is compared against the alert that started it."
                :items="joinRuleHelp"
              />
            </template>
            <template slot="alertId" slot-scope="{ record }">
              <router-link :to="'/incident/detail/' + record.alertId">{{ record.alertId }}</router-link>
            </template>
            <template slot="webpage" slot-scope="{ record }">
              <a-tooltip :title="record.webpageUrl || record.webpageName" placement="topLeft">
                <p class="mb-0">
                  <span class="ce-url ce-url--name">{{ record.webpageName }}</span>
                  <span v-if="record.webpageUrl" class="ce-url ce-url--addr">{{ record.webpageUrl }}</span>
                </p>
              </a-tooltip>
            </template>
            <template slot="detectedAt" slot-scope="{ record }">
              <p class="mb-0">
                <time>{{ record.detectedAt | dateString }}</time>
                <br />
                <time>{{ record.detectedAt | timeString }}</time>
              </p>
            </template>
            <template slot="severity" slot-scope="{ record }">
              <a-tag class="ce-severity" :style="{ backgroundColor: severityColor(record.severity), borderColor: severityColor(record.severity) }">
                {{ record.severityLabel }}
              </a-tag>
            </template>
          </PaginationTable>
        </div>
        <!-- Only the reasons on this page are listed, so the legend stays short
             enough to read. -->
        <div v-if="joinRuleLegend.length" class="ce-legend">
          <p class="ce-cardtitle">Grouping Reason</p>
          <ul class="ce-legend__list">
            <li v-for="row in joinRuleLegend" :key="row.key" class="ce-legend__item">
              <span class="ce-legend__term">{{ row.label }}</span>
              <span class="ce-legend__text">{{ row.text }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="a-footer">
        <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
      </div>
    </template>
  </div>
</template>

<script>
  import { indexItemTable } from '@shared/helpers/utils';
  import { EventAlertsColumn } from '../columnsTable/EventAlertsColumn';
  import ChangeEventsService from '../services/ChangeEvents.service';
  import {
    STATUS_LABELS,
    JOIN_RULE_LABELS,
    JOIN_RULE_LEGEND,
    labelFor,
    severityColor,
    categoryLabel,
    changeTypeLabel,
    severityMix,
    pageName,
    pageUrl,
    fileList,
    joinRuleHelpItems,
    formatNumber,
    severityLabel
  } from '../consts/ChangeEvents.consts';
  import { HELP_CONTENT } from '../consts/helpContent';
  import HelpButton from './HelpButton.vue';
  import SeverityMix from './SeverityMix.vue';
  import HeaderHelp from './HeaderHelp.vue';

  export default {
    name: 'ChangeEventDetail',
    components: { HelpButton, SeverityMix, HeaderHelp },
    data() {
      let pageSize = Number(window.localStorage.getItem('pageSize'));
      if (!pageSize) pageSize = 5;

      return {
        HELP_CONTENT,
        EventAlertsColumn,
        isLoading: true,
        event: {},
        alerts: [],
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0
        },
        tableSize: {
          height: '',
          width: ''
        }
      };
    },
    computed: {
      eventId() {
        return this.$route.params.id;
      },
      /**
       * What the Why Grouped heading explains on hover.
       *
       * Every reason, not only the ones in the rows on screen. The legend under
       * the table shows those; a person reading the heading wants to know what
       * the column can ever say.
       */
      joinRuleHelp() {
        return joinRuleHelpItems();
      },
      /** The mix of the alerts inside, or null when it was not recounted yet. */
      severityMixParts() {
        return severityMix(this.event);
      },
      categoryLabel() {
        return categoryLabel(this.event);
      },
      changeTypeLabel() {
        return changeTypeLabel(this.event);
      },
      /**
       * The one-line meaning of every "Grouping Reason" name on this page, and no
       * others.
       */
      joinRuleLegend() {
        const seen = [];
        this.alerts.forEach((row) => {
          if (row.joinRule && JOIN_RULE_LEGEND[row.joinRule] && seen.indexOf(row.joinRule) === -1) {
            seen.push(row.joinRule);
          }
        });
        return seen.map((key) => ({ key, label: JOIN_RULE_LABELS[key], text: JOIN_RULE_LEGEND[key] }));
      },
      statusLabel() {
        return labelFor(STATUS_LABELS, this.event.status);
      },
      /** `top_folders` may arrive as a list or as one comma-separated string. */
      topFolders() {
        return fileList(this.event.top_folders).files;
      },
      /**
       * The files that caused this change. The API sends them apart from the
       * full list, because they are the answer to "why are these one change?".
       */
      triggerFiles() {
        const files = this.event.changed_files || {};
        return fileList(files.trigger_files || files.trigger || files.triggers || this.event.trigger_files);
      },
      /** Every file that changed, trigger files included. */
      allChangedFiles() {
        const files = this.event.changed_files || {};
        return fileList(files.all_files || files.all || files.changed || (Array.isArray(files) ? files : null));
      },
      /** The alert that started this change, named by its page. */
      seedAlert() {
        return this.alertRef(this.event.seed_alert, this.event.seed_alert_id);
      },
      /** One alert that shows what the change looks like. */
      exampleAlert() {
        return this.alertRef(this.event.example_alert, this.event.example_alert_id);
      }
    },
    mounted() {
      this.fetchAll();
    },
    methods: {
      severityColor,
      /**
       * Turns one of the small alert objects the API attaches into something a
       * person can read. Falls back to the bare id only when the object did not
       * arrive at all, and even then says "alert", not a page number.
       * @param {object|null|undefined} alert
       * @param {number|string|null|undefined} fallbackId
       * @returns {object|null}
       */
      alertRef(alert, fallbackId) {
        if (alert && alert.alert_id != null) {
          const severity = alert.severity || '';
          return {
            alertId: alert.alert_id,
            name: pageName(alert),
            url: pageUrl(alert),
            detectedAt: alert.detected_time || alert.detected_at || alert.time_detected || null,
            severity: severity || 'unscored',
            severityLabel: severityLabel(severity)
          };
        }
        if (fallbackId == null) return null;
        return {
          alertId: fallbackId,
          name: 'Open this alert',
          url: '',
          detectedAt: null,
          severity: 'unscored',
          severityLabel: severityLabel(null)
        };
      },
      formatNumber,
      goBack() {
        this.$router.push('/change-events/list');
      },
      async fetchAll() {
        try {
          const [detail] = await Promise.all([ChangeEventsService.fetchDetail(this.eventId), this.fetchAlerts()]);
          this.event = detail || {};
        } catch (err) {
          this.$showMultiErrorMessage(err.messages);
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      /**
       * Fetches one page of the alerts inside this event. Always paginated —
       * one event can hold thousands of alerts.
       */
      async fetchAlerts() {
        const response = await ChangeEventsService.fetchEventAlerts(this.eventId, {
          page: this.pagin.current,
          perPage: this.pagin.pageSize
        });
        const rows = (response && response.data) || [];
        this.alerts = rows.map((item, index) => {
          const severity = (item.triage_result && item.triage_result.severity_level) || item.severity || '';
          return {
            index: indexItemTable(this.pagin.current, this.pagin.pageSize, index),
            id: item.alert_id,
            alertId: item.alert_id,
            // Never `url_id`. The endpoint sends the page title and its address.
            webpageName: pageName(item),
            webpageUrl: pageUrl(item),
            detectedAt: item.time_detected || item.detected_at || item.detected_time || null,
            severity: severity || 'unscored',
            severityLabel: severityLabel(severity),
            // `join_score` is deliberately not shown. It is 1.00 on nearly
            // every row, so it told a reader nothing.
            joinRule: item.join_rule,
            joinRuleLabel: labelFor(JOIN_RULE_LABELS, item.join_rule)
          };
        });
        this.pagin.total = (response && response.total) || 0;
      },
      async paginChange(pagin) {
        this.pagin = { ...pagin };
        try {
          await this.fetchAlerts();
        } catch (err) {
          this.$showMultiErrorMessage(err.messages);
        }
      },
      elSizeChange(size) {
        this.tableSize = size;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .ce-detail-head {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    &__title {
      font-size: 18px;
      font-weight: 600;
      color: #232a31;
    }
  }
  .ce-detail-lead {
    color: #5b6570;
    font-size: 13px;
    margin: 6px 0 16px;
  }
  .ce-cardtitle {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8a94a0;
    margin: 0 0 10px;
  }
  .ce-sidenote {
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 16px 18px;
    font-size: 12.5px;
    color: #5b6570;
  }
  .ce-alerts-table {
    min-height: 240px;
  }
  .ce-legend {
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 12px 16px;
    margin-top: 10px;

    &__list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    &__item {
      display: flex;
      gap: 10px;
      font-size: 12.5px;
      padding: 3px 0;
    }
    &__term {
      flex: none;
      width: 140px;
      font-weight: 600;
      color: #232a31;
    }
    &__text {
      color: #5b6570;
    }
  }
  .ce-url {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--name {
      color: #232a31;
    }
    &--addr {
      color: #8a94a0;
      font-size: 11.5px;
    }
  }
  .ce-severity.ant-tag {
    color: #ffffff;
    font-size: 10px;
  }
  .ce-panel {
    background: #ffffff;
    border: 1px solid #e3e7ea;
    border-radius: 8px;
    padding: 16px 18px;
    margin-top: 14px;
  }
  .ce-caption,
  .ce-empty {
    color: #5b6570;
    font-size: 12.5px;
    margin: 8px 0 0;
    max-width: 100ch;
  }
  .ce-alertcard {
    border: 1px solid #e3e7ea;
    border-radius: 7px;
    padding: 12px 14px;
    height: 100%;

    // The seed is the alert everything else was matched against, so it is
    // marked out from the ordinary example beside it.
    &--seed {
      border-color: #20a4b8;
      background: #f6fcfd;
    }
    &__kind {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #8a94a0;
      margin: 0 0 6px;
    }
    &__line {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }
    &__name {
      font-weight: 600;
      font-size: 13.5px;
      color: #232a31;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: #20a4b8;
        text-decoration: underline;
      }
    }
    &__url {
      color: #5b6570;
      font-size: 12px;
      margin: 4px 0 0;
      word-break: break-all;
    }
    &__when {
      color: #8a94a0;
      font-size: 12px;
      margin: 2px 0 0;
    }
    &__note {
      color: #5b6570;
      font-size: 12px;
      margin: 6px 0 0;
    }
  }
  .ce-fileshead {
    font-size: 12.5px;
    font-weight: 600;
    color: #232a31;
    margin: 12px 0 6px;

    &:first-of-type {
      margin-top: 0;
    }
  }
  .ce-files {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;

    &__more {
      color: #8a94a0;
      font-size: 12px;
    }
  }
  .ce-file.ant-tag {
    font-family: ui-monospace, Menlo, monospace;
    font-size: 11px;
    margin-right: 0;

    &--trigger {
      border-color: #20a4b8;
      color: #16788a;
      background: #f0fafb;
    }
  }
</style>
